import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { useFormikContext } from 'formik';

import { createStyle, createStyles } from '../../../utils/createStyle';

import { FieldContainer, FieldContainerProps } from './FieldContainer';

import { FieldWrapperProps } from 'components/fields/FieldWrapper';
import { demonetize, monetizeWithDecimal } from 'common/utils/strings';

export type NumberFieldProps = FieldContainerProps &
    FieldWrapperProps &
    React.InputHTMLAttributes<HTMLInputElement> & {
        inputRef?: React.Ref<HTMLInputElement>;
        min: number;
        max: number;
        increment: number;
        placeholder?: string;
        disabled?: boolean;
        prefix?: string;
        hideFieldDescription?: boolean;
        allowDecimal?: boolean;
        includeThousandsSeparator?: boolean;
        customValidator?: (_: number) => string;
    };

const styles = createStyles({
    input: [
        'w-full px-2 text-xl font-normal text-center',
        {
            background: 'transparent',
            MozAppearance: 'textfield',
            '::-webkit-outer-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
            },
            '::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
            },
            height: '2rem',
        },
    ],
    footer: createStyle('grid text-center text-xs text-grey-3 gap-2', {
        gridTemplateColumns: 'max-content max-content max-content',
    }),
    ctrlButton: createStyle('text-xl inline-block px-4'),
    ctrlContainer: createStyle('grid', {
        gridTemplateColumns: 'min-content auto min-content ',
    }),
});

// Helper to get the correct remainder for divisors up to two decimal places.
// https://stackoverflow.com/questions/3966484/why-does-modulus-operator-return-fractional-number-in-javascript
const getRemainder = (number: number, divisor: number) => {
    return (Math.round(number * 100) % Math.round(divisor * 100)) / 100;
};

export const FieldNumber = ({
    label,
    increment,
    placeholder,
    min,
    max,
    name,
    value,
    prefix,
    hideFieldDescription,
    required,
    allowDecimal = false,
    includeThousandsSeparator = false,
    customValidator,
    ...props
}: NumberFieldProps) => {
    const mask = createNumberMask({ prefix, allowDecimal, includeThousandsSeparator });
    const inputText = useRef<MaskedInput>(null);
    const getSafeValue = useCallback(() => {
        return demonetize((value || '0').toString()) || 0;
    }, [value]);

    const ctx = useFormikContext();

    const onInternalChange = (e: ChangeEvent<HTMLInputElement>) => {
        let verifiedValue = e.target.value;
        if (value === 0) {
            verifiedValue = verifiedValue.slice(0, -1);
        }
        const newValue = demonetize(verifiedValue) || 0;
        if (name) {
            ctx.setFieldValue(name, newValue, true);
        }
    };

    const setInputValidationMsg = (msg: string) => {
        if (inputText.current) {
            (inputText.current.inputElement as HTMLInputElement).setCustomValidity(msg);
        }
    };

    const getPrefix = useCallback(() => prefix || '', [prefix]);

    const [error, setError] = useState('');

    // validations
    useEffect(() => {
        const newValue = getSafeValue();

        if (newValue < min) {
            setError(`Minimum is ${getPrefix()}${min}`);
            return setInputValidationMsg(
                `Value must be greater than or equal to ${getPrefix()}${min}`
            );
        }

        if (newValue > max) {
            setError(`Maximum is ${getPrefix()}${max}`);
            return setInputValidationMsg(
                `Value must be lower than or equal to ${getPrefix()}${max}`
            );
        }

        if (getRemainder(newValue, increment) !== 0) {
            const previous = newValue - getRemainder(newValue, increment);
            const next = newValue + increment - getRemainder(newValue + increment, increment);

            setError(`Increments of ${getPrefix()}${min}`);
            return setInputValidationMsg(
                `Please enter a valid value. The two nearest valid values are ${getPrefix()}${previous} and ${getPrefix()}${next}`
            );
        }

        const customErrors = customValidator ? customValidator(newValue) : null;
        if (customErrors) {
            setError(customErrors);
            return setInputValidationMsg(customErrors);
        }

        setError('');
        return setInputValidationMsg('');
    }, [value, getSafeValue, increment, min, max, getPrefix, customValidator]);

    const _onIncrement = (direction: number) => {
        if (name) {
            const actualValue = getSafeValue();
            let newValue = actualValue + (direction > 0 ? increment : -increment);

            if (actualValue > max) {
                newValue = max;
            } else if (actualValue < min) {
                newValue = min;
            } else if (!(getRemainder(actualValue, increment) === 0)) {
                const previous = actualValue - getRemainder(actualValue, increment);
                const next =
                    actualValue + increment - getRemainder(actualValue + increment, increment);
                newValue = direction > 0 ? next : previous;
            }

            if (newValue >= min && newValue <= max) {
                ctx.setFieldValue(name, newValue, true);
            }
        }
    };

    const footer = (
        <div className={styles.footer}>
            <div>
                {getPrefix()}
                {min} - {getPrefix()}
                {max}
            </div>
            <div>
                Increments of {getPrefix()}
                {increment}
            </div>
        </div>
    );

    const inputValue = useMemo(() => {
        if (allowDecimal) {
            return monetizeWithDecimal(getSafeValue());
        }

        return getSafeValue();
    }, [allowDecimal, getSafeValue]);

    return (
        <FieldContainer
            label={label}
            footer={!hideFieldDescription && !error && footer}
            error={error}
            required={required}
        >
            <div className="flex items-center">
                <button
                    type="button"
                    className={styles.ctrlButton}
                    onClick={() => _onIncrement(-1)}
                >
                    –
                </button>
                <MaskedInput
                    {...props}
                    mask={mask}
                    value={inputValue}
                    onChange={onInternalChange}
                    ref={inputText}
                    onKeyDown={(e) => {
                        const KEY_ARROW_UP = 'ArrowUp';
                        const KEY_ARROW_DOWN = 'ArrowDown';

                        if (e.key === KEY_ARROW_UP) {
                            e.preventDefault();
                            _onIncrement(1);
                            return;
                        }

                        if (e.key === KEY_ARROW_DOWN) {
                            e.preventDefault();
                            _onIncrement(-1);
                            return;
                        }
                    }}
                    render={(ref, _props) => (
                        <input
                            {..._props}
                            type="text"
                            name={`${name}_text`}
                            placeholder={placeholder}
                            className={styles.input}
                            autoComplete="off"
                            ref={ref}
                        />
                    )}
                />
                <button type="button" className={styles.ctrlButton} onClick={() => _onIncrement(1)}>
                    +
                </button>
            </div>
        </FieldContainer>
    );
};
